import React from 'react';

interface InlineSpinnerProps {
  /** Diameter in px. Default 14, appropriate for inside a button. */
  size?: number;
  /** Stroke thickness. Default proportional to size. */
  strokeWidth?: number;
  /** Optional accessible label. Defaults to hidden decorative element. */
  label?: string;
  /** Additional class name for callers that want to compose styling. */
  className?: string;
}

/**
 * Small SVG spinner intended for use inside buttons and inputs.
 *
 * <p>Renders a full translucent track plus a rounded rotating arc — the pattern used across most
 * modern component libraries because it stays legible at 12–18px, works on any background and
 * inherits colour via {@code currentColor}. Kept as an SVG rather than a CSS-only ring so the
 * arc head remains a rounded cap at every size instead of the flat edges CSS borders produce.
 */
export const InlineSpinner: React.FC<InlineSpinnerProps> = ({
  size = 14,
  strokeWidth,
  label,
  className,
}) => {
  const width = strokeWidth ?? Math.max(1.75, size / 6);
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      role={label ? 'img' : undefined}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      className={['inline-spinner', className].filter(Boolean).join(' ')}
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth={width}
      />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth={width}
        strokeLinecap="round"
      />
    </svg>
  );
};
